import React, { ReactNode, forwardRef } from "react";
import { cn } from "@/shared/utils/shadcn";

interface PdfWrapperProps {
  children: ReactNode;
  className?: string;
  headerContent?: ReactNode;
}
// HeaderLogo component is no longer needed - logo is handled by CSS @page rules
/**
 * Wrapper for static content that will be rendered together with page breaks before and after
 * Content might span multiple pages but will always break after content
 * @deprecated Use DynamicContentWrapper or PageStartWrapper instead
 */
export const StaticContentWrapper = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn("break-before-page break-after-page", className)}
    >
      {children}
    </div>
  )
);

/**
 * Wrapper for content that will always start on a new page
 * Content can dynamically render across multiple pages
 */
export const PageStartWrapper = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div ref={ref} className={cn("break-before-page", className)}>
      {children}
    </div>
  )
);

/**
 * Wrapper for components that should not be split across pages
 * Will force a page break before the component if it would otherwise be split
 */
export const NoSplitWrapper = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div ref={ref} className={cn("break-inside-avoid", className)}>
      {children}
    </div>
  )
);

/**
 * Header component that appears on every page
 */
export function PdfHeader({ children, className }: PdfWrapperProps) {
  // Header is now handled by CSS @page rules, so this component is not needed
  return null;
}

/**
 * Footer component that appears on every page
 */
export function PdfFooter({ children, className }: PdfWrapperProps) {
  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 print:fixed print:bottom-0 z-50",
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Container for the entire PDF document
 */
export const PdfContainer = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "bg-white text-grey-900 font-sans print:shadow-none",
          "print:bg-white print:text-grey-900",
          "print:min-h-screen print:w-full",
          "print:break-after-page",
          "print:mt-0",
          "@page { margin-top: 0; margin-bottom: 0; }", // Ensure consistent page margins
          className
        )}
      >
        {children}
      </div>
    );
  }
);

export const PDFDocument = ({ pages }: { pages: React.ReactNode[] }) => pages;

/**
 * Wrapper for content sections that should not be split across pages
 * Use for complete sections like summaries, charts, or important content blocks
 */
export const ContentSection = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn(
        "pdf-content-section",
        "break-inside-avoid", // Prevent breaking inside the section
        "mb-6", // Add some margin between sections
        className
      )}
    >
      {children}
    </div>
  )
);

/**
 * Wrapper for content that should start on a new page
 * Use for major sections that should always begin on a fresh page
 */
export const NewPageSection = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn(
        "pdf-new-page-section",
        "break-before-page", // Force new page
        "break-inside-avoid", // Prevent breaking inside
        "mb-6",
        className
      )}
    >
      {children}
    </div>
  )
);

/**
 * Wrapper for content that can flow naturally across pages
 * Use for text content, lists, or other content that can break naturally
 */
export const FlowingContent = forwardRef<HTMLDivElement, PdfWrapperProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn(
        "pdf-flowing-content",
        "mb-4", // Small margin between flowing content blocks
        className
      )}
    >
      {children}
    </div>
  )
);

/**
 * Conditional section wrapper - only renders if content exists
 */
export const ConditionalSection = ({
  children,
  condition,
  wrapper: Wrapper = ContentSection,
  ...props
}: {
  children: React.ReactNode;
  condition: boolean;
  wrapper?: React.ComponentType<any>;
  [key: string]: any;
}) => {
  if (!condition) return null;

  return <Wrapper {...props}>{children}</Wrapper>;
};

/**
 * Dynamic PDF Document with single container and natural content flow
 * Content will break logically across pages with proper headers
 */
export const DynamicPDFDocument = ({
  children,
  firstPageTitle = "Document",
  className,
}: {
  children: React.ReactNode;
  firstPageTitle?: string;
  className?: string;
}) => {
  return (
    <div
      className={cn("dynamic-pdf-document", className)}
      style={
        {
          "--first-page-title": `"${firstPageTitle}"`,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};
